import React from 'react';
import {duckify, filterDuck} from "utils/duckify";
import {billingCategoryReport as billingCategoryReportDuck, schoolYears as schoolYearsDuck, fields as fieldsDuck, grades} from 'ducks';
import moment from 'moment';
import { titleize } from 'utils/strings';
import { formatCurrency } from 'utils/i18n';
import './styles/BillingCategoryReport.scss';
import queryString from 'query-string';
import { compose } from 'redux';
import { Link, withRouter } from 'react-router';
import withFiltering from 'hocs/withFiltering';
import Filters from 'components/Filters/Filters';
import MonthYearFilter from 'components/shared/MonthYearFilter';
import history from 'shared_history';
import FlashRoot from 'components/FlashMessage/FlashRoot';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';
import Placeholder from '../Placeholder';
import BulkAction from '../ContactList/BulkActions/BulkAction';
import BulkActions from '../ContactList/BulkActions/BulkActions';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';

const MAX_MONTH_RANGE = 15;

const BillingCategoryReport = ({
  billingCategoryReport, allDucksReady, location, schoolYears, filters, setFilters, additionalFields, grades
}) => {
  if (allDucksReady) {
    const monthYearRef = React.useRef(null);
    const [pendingDates, setPendingDates] = React.useState(null);
    const schoolYearItems = schoolYears.map(y => [y.name, y.id]);
    schoolYearItems.push(['no term', 'no-term']);

    const filterFields = [
      {
        solr_prompt: 'school_year_id',
        display_name: 'Term',
        options: schoolYearItems
      },
      {
        solr_prompt: 'grade',
        display_name: 'Grade',
        options: grades.map(g => [g.name, g.id])
      }
    ];
    const report = billingCategoryReport[0].billing_category_totals;
    const dateHeaders = billingCategoryReport[0].date_headers;
    const query = queryString.parse(location.search);
    const startDate = query.start_date;
    const endDate = query.end_date;
    const ReportRow = ({ row, categoryName, subCategory }) => {
      const totals = subCategory === 'totals';
      return (
        <tr className={totals ? 'billing-categories-category-row' : ''}>
          <td className={totals ? 'bold' : 'normal'}>{totals ? `${categoryName} Totals` : titleize(subCategory)}</td>
          <td className={totals ? '' : 'billing-category-report-sub-total'}>
            {totals ? (
              <GenerateLink
                billingCategoryId={row.billing_category_id}
                startDate={startDate}
                endDate={endDate}
                amount={formatCurrency(row.total || 0)}
              />
            ) : formatCurrency(row.total || 0)
            }
          </td>
          {dateHeaders.map(([year, month]) => (
            <td className={totals ? '' : 'billing-category-report-sub-total'}>
              {totals ? (
                <GenerateLink
                  billingCategoryId={row.billing_category_id}
                  startDate={moment([year, month - 1, 1])}
                  endDate={moment([year, month - 1, 1]).endOf('month')}
                  amount={row[year] ? formatCurrency(row[year][month] || 0) : formatCurrency(0)}
                />
              ) : row[year] ? formatCurrency(row[year][month] || 0) : formatCurrency(0)
              }
            </td>
          ))}
        </tr>
      );
    };

    const SummaryRow = ({ row }) => (
      <tr className='billing-categories-category-row'>
        <td className='bold'>Total</td>
        <td className='billing-category-report-sub-total'>
          <GenerateLink
            billingCategoryId=''
            startDate={startDate}
            endDate={endDate}
            amount={formatCurrency(row.total || 0)}
          />
        </td>
        {dateHeaders.map(([year, month]) => (
          <td>
            {<GenerateLink
              billingCategoryId=''
              startDate={moment([year, month - 1, 1])}
              endDate={moment([year, month - 1, 1]).endOf('month')}
              amount={row[year] ? formatCurrency(row[year][month] || 0) : formatCurrency(0)}
            />
            }
          </td>
        ))}
      </tr>
    );

    return (
      <div>
        <div className='billing-category-report-space-between'>
          <h2>Billing Category Report</h2>

          <BulkActions>
            <BulkAction title='Export' icon={faSignOut}>
              <BulkExportAction
                type='BillingCategoryReportCSVJob'
                location={location}
                url='/api/billing_category_report/export.csv'
              />
              <BulkExportAction
                title='Export to PDF'
                type='BillingCategoryReportCSVJob'
                location={location}
                url='/api/billing_category_report/export.pdf'
              />
            </BulkAction>
          </BulkActions>
        </div>

        <p>
          Your Billing Category Report downloads will be available&nbsp;
          <Link to='/setup/user_jobs'>here</Link>
          .
        </p>

        <FlashRoot />

        <MonthYearFilter ref={monthYearRef} location={location} maxMonthRange={MAX_MONTH_RANGE} onDatesChange={(d) => setPendingDates(d)} />
        <Filters
          fields={filterFields.concat(additionalFields)}
          selected={filters}
          submitFilters={(newFilters) => {
            const qs = queryString.parse(location.search);
            const dates = pendingDates || {
              start_date: qs.start_date,
              end_date: qs.end_date
            };

            const nextQuery = {
              ...qs,
              ...dates,
              filters: JSON.stringify(newFilters)
            };
            Object.keys(nextQuery).forEach((k) => { if (nextQuery[k] === undefined) delete nextQuery[k]; });

            history.push({ ...location, search: `?${queryString.stringify(nextQuery)}` });
            setPendingDates(null);
          }}
        />
        <div className='scrollable'>
          <table className='table billing-categories-report-table'>
            <thead>
            <th>Billing Category</th>
            <th>Total</th>
            {dateHeaders.map(([year, month]) => (
              <th>
                {' '}
                {year}
                -
                {moment(month, 'MM').format('MMM')}
              </th>
            ))}
            </thead>
            {Object.keys(report).length > 0 ? (
              <tbody>
              {Object.keys(report).map((categoryName) => (
                Object.keys(report[categoryName]).map((subCategory) => (categoryName !== 'summary'
                  ? (
                    <ReportRow
                      row={report[categoryName][subCategory]} categoryName={categoryName}
                      subCategory={subCategory}
                    />
                  ) : ''))
              ))}
              <SummaryRow row={report.summary} />
              </tbody>
            ) : <div>No results found.</div>}
          </table>
        </div>
      </div>
    );
  }

  return <Placeholder.loading />;
};


const GenerateLink = ({
  billingCategoryId, startDate, endDate, amount
}) => {
  const query = queryString.parse(location.search);
  const queryFilters = query.filters ? JSON.parse(query.filters) : {};
  const categoryFilter = billingCategoryId ? { billing_category_parent_id: [billingCategoryId] } : {};
  const filters = JSON.stringify({ ...categoryFilter, ...queryFilters });
  const queryParams = queryString.stringify({
    filters,
    range: 'custom',
    from: startDate,
    to: endDate
  });
  return (
    <a className='category-report-link' href={`/admin/billing/ledger?${queryParams}`}>
      {' '}
      {formatCurrency(amount || 0)}
    </a>
  );
};

const mapDucksToProps = () => {
  // Ensure dates are always present even if URL has only filters
  const qs = location.search ? { ...queryString.parse(location.search) } : {};
  const defaultDateRange = {
    ...qs,
    start_date: qs.start_date || moment().startOf('month').format('YYYY-MM-DD'),
    end_date: qs.end_date || moment().endOf('month').format('YYYY-MM-DD')
  };
  return {
    billingCategoryReport: filterDuck(billingCategoryReportDuck, defaultDateRange),
    schoolYears: schoolYearsDuck,
    additionalFields: filterDuck(fieldsDuck, {for: 'billing'}),
    grades
  }
}

export default compose(
  withRouter,
  withFiltering,
  duckify(mapDucksToProps)
)(BillingCategoryReport);
